import React, { useState, useEffect, useRef } from 'react';
import { 
  FaUsers, 
  FaDownload, 
  FaTimes, 
  FaChevronLeft, 
  FaChevronRight, 
  FaUserPlus, 
  FaEdit, 
  FaExchangeAlt, 
  FaInfoCircle, 
  FaTrashAlt, 
  FaSearch,
  FaFilter,
  FaBuilding,
  FaUserTie,
  FaMapMarkedAlt
} from 'react-icons/fa';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import * as XLSX from 'xlsx';
import styles from './CenterManagementPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TableFilter from '../../components/TableFilter';
import AddCenterModal from './AddCenterModal';
import SMOOverviewModal from './SMOOverviewModal';
import RegionManagementModal from './RegionManagementModal';
import { findRegionByCity, getResponsibleManagerIds } from './regionUtils';
import { useTheme } from '../../contexts/ThemeContext';
import './CenterManagementDarkMode.css';

const truncateText = (text, limit = 30) => {
  if (!text) return '-';
  return text.length > limit ? `${text.substring(0, limit)}...` : text;
};

const CenterManagementPage = () => {
  const { isDarkMode } = useTheme();
  const [centers, setCenters] = useState([]);
  const [filteredCenters, setFilteredCenters] = useState([]);
  const [employeesMap, setEmployeesMap] = useState({});
  const [filters, setFilters] = useState({ 'Durum': 'Aktif' });
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSmoOverviewOpen, setIsSmoOverviewOpen] = useState(false);
  const [isRegionModalOpen, setIsRegionModalOpen] = useState(false);
  const [regions, setRegions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    'Adı': true,
    'SMO': true,
    'Yedek SMO': true,
    'Bölge Müdürü': true,
    'Adres': true,
    'Bilgi': true,
    'Durum': true,
    'İl': true,
    'İlçe': true
  });
  
  const tableContainerRef = useRef(null);
  const filtersRef = useRef(null);

  const columns = {
    'Adı': { label: 'Adı', type: 'text' },
    'SMO': { label: 'SMO', type: 'text' },
    'Yedek SMO': { label: 'Yedek SMO', type: 'text' },
    'Bölge Müdürü': { label: 'Bölge Sorumlusu', type: 'text' },
    'Adres': { label: 'Adres', type: 'text' },
    'Bilgi': { label: 'Bilgi', type: 'text' },
    'Durum': { 
      label: 'Durum', 
      type: 'select',
      options: [
        { value: "", label: "Tümü" },
        { value: "Aktif", label: "Aktif" },
        { value: "İnaktif", label: "İnaktif" }
      ]
    },
    'İl': { label: 'İl', type: 'text' },
    'İlçe': { label: 'İlçe', type: 'text' }
  };

  const fetchCenters = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Centers'));
      const centersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCenters(centersData);
      setFilteredCenters(centersData);
    } catch (error) {
      console.error('Error fetching centers:', error);
      toast.error('Merkezler yüklenirken bir hata oluştu');
    }
  };

  const fetchEmployees = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'Employees'));
      const map = {};
      snapshot.docs.forEach(doc => {
        const d = doc.data();
        map[doc.id] = `${d.eName || ''} ${d.eSurname || ''}`.trim();
      });
      setEmployeesMap(map);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const getSMOName = (center) => {
    const raw = center?.SMO;
    if (!raw) return '';
    return employeesMap[raw] || raw;
  };

  const getSMOBackupName = (center) => {
    const raw = center?.SMOBackup;
    if (!raw) return '';
    return employeesMap[raw] || raw;
  };

  const fetchRegions = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'Regions'));
      setRegions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const getRegionForCenter = (center) => findRegionByCity(regions, center?.['İl']);

  const getRegionManagerName = (center) => {
    const region = getRegionForCenter(center);
    if (!region) return '';
    return getResponsibleManagerIds(region, center?.['İl'])
      .map((managerId) => employeesMap[managerId])
      .filter(Boolean)
      .join(', ');
  };

  useEffect(() => {
    fetchCenters();
    fetchEmployees();
    fetchRegions();
    
    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  useEffect(() => {
    let filtered = centers;

    // Apply search term first
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        center => 
          (center['Adı'] && center['Adı'].toString().toLowerCase().includes(lowercasedSearch)) ||
          (center['İl'] && center['İl'].toString().toLowerCase().includes(lowercasedSearch)) ||
          (center['İlçe'] && center['İlçe'].toString().toLowerCase().includes(lowercasedSearch))
      );
    }

    // Then apply individual column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        if (key === 'Durum') {
          filtered = filtered.filter(center => center[key] === filters[key]);
        } else if (key === 'SMO') {
          filtered = filtered.filter(center =>
            getSMOName(center)
              .toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        } else if (key === 'Yedek SMO') {
          filtered = filtered.filter(center =>
            getSMOBackupName(center)
              .toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        } else if (key === 'Bölge Müdürü') {
          filtered = filtered.filter(center =>
            getRegionManagerName(center)
              .toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        } else {
          filtered = filtered.filter(center =>
            String(center[key])
              ?.toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        }
      }
    });

    setFilteredCenters(filtered);
  }, [centers, filters, searchTerm, regions, employeesMap]);

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const activeCount = centers.filter(center => center['Durum'] === 'Aktif').length;
  const inactiveCount = centers.filter(center => center['Durum'] === 'İnaktif').length;

  const downloadExcel = () => {
    try {
      const dataToExport = centers.map(center => {
        const exportData = {};
        Object.keys(visibleColumns).forEach(key => {
          if (visibleColumns[key]) {
            if (key === 'SMO') {
              exportData[columns[key].label] = getSMOName(center);
            } else if (key === 'Yedek SMO') {
              exportData[columns[key].label] = getSMOBackupName(center);
            } else if (key === 'Bölge Müdürü') {
              exportData[columns[key].label] = getRegionManagerName(center);
            } else {
              exportData[columns[key].label] = center[key] || '';
            }
          }
        });
        return exportData;
      });

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Merkezler');
      XLSX.writeFile(workbook, 'MerkezListesi.xlsx');
      
      toast.success('Excel dosyası başarıyla indirildi');
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error('Dosya indirilirken bir hata oluştu');
    }
  };

  const handleCenterSelect = (center) => {
    setSelectedCenter(center);
  };

  const handleStatusToggle = async (center = null) => {
    const targetCenter = center || selectedCenter;
    if (!targetCenter) return;
    
    const newStatus = targetCenter['Durum'] === 'Aktif' ? 'İnaktif' : 'Aktif';
    
    try {
      const centerRef = doc(db, 'Centers', targetCenter.id);
      await updateDoc(centerRef, { 'Durum': newStatus });
      
      // Update the selectedCenter state with the new status
      setSelectedCenter(prev => ({
        ...prev,
        'Durum': newStatus
      }));
      
      // Update the centers and filteredCenters states
      setCenters(prev => prev.map(c => 
        c.id === targetCenter.id ? { ...c, 'Durum': newStatus } : c
      ));
      setFilteredCenters(prev => prev.map(c => 
        c.id === targetCenter.id ? { ...c, 'Durum': newStatus } : c
      ));
      
      toast.success(`Merkez durumu ${newStatus} olarak güncellendi`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Durum güncellenirken hata oluştu');
    }
  };

  const [centerToDelete, setCenterToDelete] = useState(null);

  const openDeleteModal = async (center) => {
    // Check if center is used in any projects
    try {
      const projectsSnapshot = await getDocs(collection(db, 'Projects'));
      let isUsedInProjects = false;
      
      for (const projectDoc of projectsSnapshot.docs) {
        const projectData = projectDoc.data();
        const employees = projectData.employees || [];
        
        // Check if any employee is assigned to this center
        if (employees.some(emp => emp.centerId === center.id)) {
          isUsedInProjects = true;
          break;
        }
      }
      
      setCenterToDelete({ ...center, isUsedInProjects });
      setIsDeleteModalOpen(true);
    } catch (error) {
      console.error('Error checking center usage:', error);
      toast.error('Merkez kontrol edilirken bir hata oluştu');
    }
  };

  const handleDeleteCenter = async () => {
    if (!centerToDelete) return;
    
    try {
      console.log('🗑️ Deleting center:', centerToDelete.id, centerToDelete.Adı);
      console.log('📋 Center SMO:', centerToDelete.SMO);
      
      // Before deleting, add SMO info to all employees in Projects collection that use this center
      const projectsSnapshot = await getDocs(collection(db, 'Projects'));
      let updatedProjectsCount = 0;
      let updatedEmployeesCount = 0;
      
      for (const projectDoc of projectsSnapshot.docs) {
        const projectData = projectDoc.data();
        const employees = projectData.employees || [];
        
        // Find employees using this center
        let hasChanges = false;
        const updatedEmployeesList = employees.map(emp => {
          if (emp.centerId === centerToDelete.id) {
            // Add SMO info if not already present
            if (!emp.smoId && centerToDelete.SMO) {
              hasChanges = true;
              updatedEmployeesCount++;
              console.log(`✅ Adding SMO ${centerToDelete.SMO} to employee ${emp.name} ${emp.surname} in project ${projectDoc.id}`);
              return {
                ...emp,
                smoId: centerToDelete.SMO,
                centerDeletedAt: new Date().toISOString() // Mark when center was deleted
              };
            }
          }
          return emp;
        });
        
        // Update project if there were changes
        if (hasChanges) {
          await updateDoc(doc(db, 'Projects', projectDoc.id), {
            employees: updatedEmployeesList
          });
          updatedProjectsCount++;
        }
      }
      
      console.log(`📊 Updated ${updatedEmployeesCount} employees in ${updatedProjectsCount} projects`);
      
      // Now delete the center
      await deleteDoc(doc(db, 'Centers', centerToDelete.id));
      
      toast.success(`Merkez başarıyla silindi. ${updatedEmployeesCount} çalışan kaydı güncellendi.`);
      setIsDeleteModalOpen(false);
      setCenterToDelete(null);
      fetchCenters();
    } catch (error) {
      console.error('Error deleting center:', error);
      toast.error('Merkez silinirken bir hata oluştu');
    }
  };

  const handleAddCenter = async (centerData) => {
    try {
      console.log('New center data:', centerData);
      setIsAddModalOpen(false);
      await fetchCenters();
      toast.success('Yeni merkez başarıyla eklendi');
    } catch (error) {
      console.error('Error adding center:', error);
      toast.error('Merkez eklenirken bir hata oluştu');
    }
  };

  const handleEditCenter = (center) => {
    setSelectedCenter(center);
    setIsEditModalOpen(true);
  };

  const handleCenterUpdate = () => {
    setIsEditModalOpen(false);
    fetchCenters();
    toast.success('Merkez başarıyla güncellendi!');
  };

  useEffect(() => {
    const checkScroll = () => {
      if (tableContainerRef.current) {
        const { scrollWidth, clientWidth } = tableContainerRef.current;
        const hasScroll = scrollWidth > clientWidth;
        setHasHorizontalScroll(hasScroll);
      }
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [centers, filteredCenters]);

  const handleScroll = (e) => {
    // Prevent the scroll event from propagating to parent elements
    e.stopPropagation();
    
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + 
        (direction === 'right' ? scrollAmount : -scrollAmount);
      
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll({ stopPropagation: () => {} }); // Initial check with dummy event
      
      // Prevent wheel events from propagating beyond the table container
      const preventWheelPropagation = (e) => {
        // Check if the table actually needs scrolling
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } = container;
        const hasVerticalScroll = scrollHeight > clientHeight;
        const hasHorizontalScroll = scrollWidth > clientWidth;
        
        // Only handle the event if there's something to scroll
        if (hasVerticalScroll || hasHorizontalScroll) {
          // Always prevent the wheel event from affecting the page
          e.stopPropagation();
          e.preventDefault();
          
          // Manually handle the scroll within the table container
          const scrollAmount = e.deltaY || e.deltaX;
          if (e.shiftKey || (!hasVerticalScroll && hasHorizontalScroll)) {
            // Horizontal scroll if shift is pressed or only horizontal scrolling is available
            container.scrollLeft += scrollAmount;
          } else {
            // Vertical scroll
            container.scrollTop += scrollAmount;
          }
        }
      };
      
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', preventWheelPropagation, { passive: false });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', preventWheelPropagation);
      };
    }
  }, [centers, filteredCenters]);

  // Apply dark mode class to document body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <div>
              <h1 className={styles.title}>
                <FaBuilding className={styles.headerIcon} />
                Merkez Yönetimi
              </h1>
              <div className={styles.breadcrumb}>Centers Menu / Centers Management</div>
            </div>
            <div className={styles.headerButtons}>
              <button
                className={styles.addButton}
                onClick={() => setIsAddModalOpen(true)}
              >
                <FaUserPlus className={styles.buttonIcon} /> Yeni Merkez Ekle
              </button>
              <button
                className={styles.downloadButton}
                onClick={() => setIsRegionModalOpen(true)}
                title="Türkiye haritası üzerinden bölgeleri ve bölge müdürlerini yönetin"
              >
                <FaMapMarkedAlt className={styles.buttonIcon} /> Bölge Yönetimi
              </button>
              <button
                className={styles.downloadButton}
                onClick={() => setIsSmoOverviewOpen(true)}
                title="SMO atamalarını detaylı görüntüle"
              >
                <FaUserTie className={styles.buttonIcon} /> SMO Genel Bakış
              </button>
              <button 
                className={styles.downloadButton} 
                onClick={downloadExcel}
              >
                <FaDownload className={styles.buttonIcon} /> Tabloyu İndir
              </button>
            </div>
          </div>
        </header>

        <div className={`${styles.dashboard} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.statsCards}>
            <div className={styles.card}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaBuilding />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{centers.length}</p>
                  <h3 className={styles.cardTitle}>Toplam Merkez</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.activeCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaBuilding />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{activeCount}</p>
                  <h3 className={styles.cardTitle}>Aktif Merkez</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.inactiveCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaBuilding />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{inactiveCount}</p>
                  <h3 className={styles.cardTitle}>İnaktif Merkez</h3>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.searchFilterContainer}>
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Merkez adı, il veya ilçe ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button 
                  className={styles.clearSearch} 
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
            </div>
            
            <div className={styles.filterContainer} ref={filtersRef}>
              <button 
                className={styles.filterButton}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className={styles.filterIcon} />
                <span>Filtreler</span>
                {Object.keys(filters).length > 0 && (
                  <span className={styles.filterBadge}>
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>
              
              {showFilters && (
                <div className={styles.filterDropdown}>
                  <div className={styles.filterHeader}>
                    <h3>Filtreler</h3>
                    <button 
                      className={styles.clearFiltersBtn}
                      onClick={clearFilters}
                    >
                      Tümünü Temizle
                    </button>
                  </div>
                  
                  <div className={styles.columnVisibility}>
                    <h4>Sütun Görünürlüğü</h4>
                    <div className={styles.columnCheckboxes}>
                      {Object.keys(columns).map(colKey => (
                        <div key={colKey} className={styles.columnCheckbox}>
                          <input
                            type="checkbox"
                            id={`col-${colKey}`}
                            checked={visibleColumns[colKey]}
                            onChange={() => toggleColumnVisibility(colKey)}
                          />
                          <label htmlFor={`col-${colKey}`}>
                            {columns[colKey].label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedCenter && (
          <div className={`${styles.actionPanel} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.selectedCenterInfo}>
              <h3 className={styles.selectedName}>
                {selectedCenter['Adı']}
              </h3>
              <span className={`${styles.statusBadge} ${
                selectedCenter['Durum'] === 'Aktif' 
                  ? styles.statusActive 
                  : styles.statusInactive
              }`}>
                {selectedCenter['Durum']}
              </span>
            </div>
            
            <div className={styles.actionButtons}>
              <button
                className={`${styles.actionButton} ${styles.deleteActionButton}`}
                onClick={() => openDeleteModal(selectedCenter)}
                title="Sil"
              >
                <FaTrashAlt className={styles.actionIcon} />
                <span>Sil</span>
              </button>
              
              <button
                className={styles.actionButton}
                onClick={() => handleEditCenter(selectedCenter)}
                title="Düzenle"
              >
                <FaEdit className={styles.actionIcon} />
                <span>Düzenle</span>
              </button>
              
              <button className={`${styles.actionButton} ${styles.statusButton}`} onClick={() => handleStatusToggle(selectedCenter)}>
                <FaExchangeAlt className={styles.actionIcon} />
                <span>Durum Değiştir</span>
              </button>
            </div>
          </div>
        )}

        <section className={`${styles.tableSection} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={`${styles.tableWrapper} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={`${styles.tableScrollContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
              {showLeftScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollRight}`}
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              <div
                ref={tableContainerRef}
                className={`${styles.tableContainer} ${isDarkMode ? 'dark-mode' : ''}`}
                onScroll={handleScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                <table className={`${styles.table} ${isDarkMode ? 'dark-mode' : ''}`}>
                  <thead>
                    <tr>
                      {Object.entries(columns).map(([key, col]) => 
                        visibleColumns[key] && (
                          <th key={key}>
                            {col.label}
                            <TableFilter 
                              column={key} 
                              type={col.type} 
                              options={col.options} 
                              onFilter={handleFilter}
                              currentValue={filters[key] || ''} 
                            />
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCenters.length > 0 ? (
                      filteredCenters.map((center) => (
                        <tr 
                          key={center.id} 
                          onClick={() => handleCenterSelect(center)}
                          className={center.Durum === 'İnaktif' ? styles.inactiveRow : ''}
                        >
                          {Object.keys(columns).map(key => 
                            visibleColumns[key] && (
                              <td key={key} title={
                                key === 'SMO' ? getSMOName(center)
                                : key === 'Yedek SMO' ? getSMOBackupName(center)
                                : key === 'Bölge Müdürü' ? (
                                    getRegionForCenter(center)
                                      ? `${getRegionForCenter(center).name}: ${getRegionManagerName(center) || 'Sorumlu atanmadı'}`
                                      : 'Bölge atanmadı'
                                  )
                                : center[key]
                              }>
                                {key === 'Durum' ? (
                                  <span className={`${styles.statusBadge} ${
                                    center['Durum'] === 'Aktif'
                                      ? styles.statusActive
                                      : styles.statusInactive
                                  }`}>
                                    {center['Durum'] === 'Aktif' ? 'Aktif' : 'İnaktif'}
                                  </span>
                                ) : key === 'SMO' ? (
                                  truncateText(getSMOName(center))
                                ) : key === 'Yedek SMO' ? (
                                  truncateText(getSMOBackupName(center))
                                ) : key === 'Bölge Müdürü' ? (
                                  (() => {
                                    const region = getRegionForCenter(center);
                                    if (!region) return '-';
                                    return (
                                      <span className={styles.regionCell}>
                                        <span
                                          className={styles.regionDot}
                                          style={{ backgroundColor: region.color }}
                                        />
                                        {truncateText(getRegionManagerName(center)) || '-'}
                                      </span>
                                    );
                                  })()
                                ) : (
                                  truncateText(center[key])
                                )}
                              </td>
                            )
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={Object.keys(visibleColumns).filter(key => visibleColumns[key]).length} className={styles.noDataCell}>
                          <div className={styles.noData}>
                            <FaBuilding className={styles.noDataIcon} />
                            <p>Filtrelere uygun merkez bulunamadı</p>
                            {(Object.keys(filters).length > 0 || searchTerm) && (
                              <button className={styles.clearFiltersButton} onClick={clearFilters}>
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>

      <AddCenterModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={() => {
          setIsAddModalOpen(false);
          fetchCenters();
        }}
      />
      
      <AddCenterModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleCenterUpdate}
        centerData={selectedCenter}
        isEditing={true}
      />

      <RegionManagementModal
        isOpen={isRegionModalOpen}
        onClose={() => setIsRegionModalOpen(false)}
        employeesMap={employeesMap}
        onSaved={fetchRegions}
        centers={centers}
      />

      <SMOOverviewModal
        isOpen={isSmoOverviewOpen}
        onClose={() => setIsSmoOverviewOpen(false)}
        centers={centers}
        employeesMap={employeesMap}
        onCenterUpdated={fetchCenters}
      />

      {isDeleteModalOpen && centerToDelete && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <FaTrashAlt className={styles.modalIcon} />
                Merkez Sil
              </h2>
              <button 
                className={styles.closeButton}
                onClick={() => setIsDeleteModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.deleteWarning}>
                <strong>{centerToDelete['Adı']}</strong> merkezini silmek istediğinizden emin misiniz?
              </p>
              
              {centerToDelete.isUsedInProjects && (
                <div className={styles.infoBox}>
                  <FaInfoCircle className={styles.infoIcon} />
                  <div className={styles.infoText}>
                    <strong>Önemli Bilgi:</strong>
                    <p>
                      Bu merkez SMO Lead Raporlarında kullanılmaktadır. 
                      Merkezi silseniz bile, SMO Lead Raporlarındaki bu merkezle ilgili veriler korunacaktır.
                    </p>
                  </div>
                </div>
              )}
              
              <p className={styles.deleteNote}>
                Bu işlem geri alınamaz.
              </p>
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={`${styles.actionButton} ${styles.cancelButton}`}
                onClick={() => setIsDeleteModalOpen(false)}
              >
                İptal
              </button>
              <button 
                className={`${styles.actionButton} ${styles.deleteButton}`}
                onClick={handleDeleteCenter}
              >
                <FaTrashAlt className={styles.actionIcon} />
                Merkezi Sil
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />
    </div>
  );
};

export default CenterManagementPage;
